import React, { MouseEvent, MouseEventHandler, useCallback } from 'react';
import BasicIcon from '../../../BasicIcon/BasicIcon';
import { ID } from '../../../utils/arrays';
import {
  ContextInfoBox,
  ContextInfoContainer,
  ContextInfoLabel,
  ContextInfoSubLabel,
  ContextItemBox,
  ContextItemLabel,
  ContextMenuBox,
  ContextMenuContainer,
  VersionContainer,
  VersionLabel,
} from './styles';
import { ContextMenuItem, ContextMenuProps, ContextMenuUser } from './types';

export const normalizeLabel = (label: string) => label.toLocaleLowerCase().trim().replace(' ', '_');

export const ContextMenu: React.FC<ContextMenuProps> = ({
  contextMenu,
  isOpen,
  testId,
  onClose,
  version,
  labelVersion = 'Versión',
}) => {
  const onClick = useCallback(
    (action: MouseEventHandler<HTMLDivElement>) => (event: MouseEvent<HTMLDivElement>) => {
      onClose();
      action(event);
    },
    [onClose],
  );

  const renderUserData = useCallback(
    ({ label, subLabel, iconName, highlighted }: ContextMenuUser) => (
      <ContextInfoBox data-testId={`${testId}-user-data`}>
        {iconName && <BasicIcon name={iconName} />}
        <ContextInfoContainer>
          <ContextInfoLabel>{label}</ContextInfoLabel>
          <ContextInfoSubLabel highlighted={highlighted}>{subLabel}</ContextInfoSubLabel>
        </ContextInfoContainer>
      </ContextInfoBox>
    ),
    [testId],
  );

  const renderContent = useCallback(
    ({ label, action, iconName, id }: ContextMenuItem) => (
      <>
        <ContextItemBox
          key={ID()}
          onClick={onClick(action)}
          id={`cm-item-${normalizeLabel(label)}`}
          data-testId={`${testId}-${id}`}
        >
          {iconName && <BasicIcon name={iconName} />}
          <ContextItemLabel>{label}</ContextItemLabel>
        </ContextItemBox>
      </>
    ),
    [onClick, testId],
  );

  return (
    <ContextMenuContainer isOpen={isOpen} data-testid={testId}>
      <ContextMenuBox>
        {contextMenu.userData && renderUserData(contextMenu.userData)}
        {contextMenu.items.map(
          (contextItem: ContextMenuItem) => !contextItem.hidden && renderContent(contextItem),
        )}
        {version && (
          <VersionContainer>
            <VersionLabel>{`${labelVersion}: ${version}`}</VersionLabel>
          </VersionContainer>
        )}
      </ContextMenuBox>
    </ContextMenuContainer>
  );
};

export default ContextMenu;
